import L from 'leaflet';
import { clone, filter, forEach, map, uniq } from 'lodash';

const DEFAULTS = {};

/**
 * @extends {L.Evented}
 */
class LayerManagement extends L.Evented {
	/**
	 * @param {Widget} widget
	 */
	constructor(widget, options) {
		super();

		this.widget = widget;
		this.options = Object.assign({}, DEFAULTS, options);
		this.addEventParent(widget);

		this._layers = {};
	}

	/**
	 * @param {L.Layer} layer
	 */
	addLayer(layer) {
		if (this._layers[layer.options.name] === undefined) {
			if (!layer.options.group) {
				layer.options.group = 'default';
			}

			layer.addEventParent(this);

			this._layers[layer.options.name] = layer;

			if (typeof layer.setTime === 'function') {
				this.widget.time.addLayer(layer);
			}

			if (layer.options.visible) {
				this.showLayer(layer.options.name);
			}

			this.fire('LAYERS_LAYER_ADDED', { layer });
		}
	}

	/**
	 * @param {string} name
	 */
	removeLayer(name) {
		let layer = this._layers[name];

		if (layer) {
			this.hideLayer(name);
			this.widget.time.removeLayer(layer.options.name);

			delete this._layers[name];

			this.fire('LAYERS_LAYER_REMOVED', { layer });
		}
	}

	/**
	 * @param {string} name
	 */
	showLayer(name) {
		let layer = this._layers[name];

		if (layer && !layer._map) {
			this.widget.map.addLayer(layer);

			this.fire('LAYERS_LAYER_SHOWN', { layer });

			if (layer.options.includedLayers) {
				layer.options.includedLayers.forEach(included => {
					this.showLayer(included);
				});
			}

			if (layer.options.excludedLayers) {
				layer.options.excludedLayers.forEach(excluded => {
					this.hideLayer(excluded);
				});
			}
		}
	}

	/**
	 * @param {string} name
	 */
	hideLayer(name) {
		let layer = this._layers[name];

		if (layer && layer._map) {
			this.widget.map.removeLayer(layer);

			this.fire('LAYERS_LAYER_HIDDEN', { layer });
		}
	}

	/**
	 * @param {string} name
	 */
	toggleLayer(name) {
		let layer = this._layers[name];

		if (layer && layer._map) {
			this.hideLayer(name);
		} else {
			this.showLayer(name);
		}
	}

	/**
	 * @param {string} name
	 * @returns {L.Layer}
	 */
	getLayer(name) {
		return this._layers[name];
	}

	/**
	 * @returns {L.Layer[]}
	 */
	getAllLayers() {
		return clone(this._layers);
	}

	/**
	 * @returns {L.Layer[]}
	 */
	getVisibleLayers() {
		return filter(this._layers, layer => layer._map);
	}

	/**
	 * @returns {string[]}
	 */
	getGroups() {
		return uniq(map(this._layers, layer => layer.options.group));
	}

	/**
	 * @param {string} name
	 * @returns {L.Layer[]}
	 */
	getLayersByGroup(name) {
		let layers = filter(this._layers, layer => layer.options.group === name);

		return layers;
	}

	/**
	 *
	 */
	destroy() {
		forEach(this._layers, layer => {
			this.removeLayer(layer.options.name);
		});

		this.removeEventParent(this.widget);
	}
}

export function layerManagement(widget, options) {
	return new LayerManagement(widget, options);
}

export default LayerManagement;
